import { Box, Button, CircularProgress, Tab, Tabs, Typography } from "@mui/material";
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Fetch from "../../services/Fetch";
import GridMediaCard from "../../components/cards/GridMediaCard";
import PageHero from "../../components/motion/PageHero";
import ScrollReveal from "../../components/motion/ScrollReveal";
import { useConstants } from "../../hooks/UseConstants";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import CertificateVerifyPanel from "../../components/certificates/CertificateVerifyPanel";
import useSnackBar from "../../hooks/UseSnackBar";
import SnackbarAlert from "../../components/SnackBar";

function formatDate(value, isAr) {
    if (!value) return isAr ? 'غير متوفر' : 'Unavailable';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return new Intl.DateTimeFormat(isAr ? 'ar-EG' : 'en-US', { dateStyle: 'medium' }).format(parsed);
}

function Certificates() {
    const { host, language } = useConstants();
    const isAr = language === 'ar';
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);

    const tab = useMemo(() => (searchParams.get('tab') === 'verify' ? 'verify' : 'list'), [searchParams]);

    const setTab = (next) => {
        if (next === 'verify') setSearchParams({ tab: 'verify' }, { replace: true });
        else setSearchParams({}, { replace: true });
    };

    useEffect(() => {
        const load = async () => {
            const result = await Fetch(`${host}/student/certificates`, 'GET');
            if (result.status === 200) setItems(result.data.data || []);
            setLoading(false);
        };
        load();
    }, [host]);

    const download = async (id) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${host}/student/certificates/${id}/download`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const blob = await res.blob();
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `certificate-${id}.pdf`;
        link.click();
    };

    const copyCode = async (code) => {
        if (!code) return;
        try {
            await navigator.clipboard.writeText(code);
            setSnackBar('success', isAr ? 'تم نسخ رمز التحقق' : 'Verification code copied');
        } catch {
            setSnackBar('info', code);
        }
    };

    const openVerify = (code) => {
        if (code) {
            navigate(`/verify-certificate?code=${encodeURIComponent(code)}`);
            return;
        }
        setTab('verify');
    };

    if (loading) return <Box className="flex justify-center py-20"><CircularProgress size={70} /></Box>;

    return (
        <Box>
            <PageHero
                title={isAr ? 'الشهادات والتحقق' : 'Certificates & verification'}
                subtitle={isAr
                    ? 'حمّل شهاداتك أو تحقق من أي شهادة عبر الرمز أو QR حسب سيناريو المنصة'
                    : 'Download your certificates or verify any certificate via code or QR'}
                icon={<WorkspacePremiumOutlinedIcon />}
            />

            <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
                <Tab value="list" icon={<WorkspacePremiumOutlinedIcon />} iconPosition="start" label={isAr ? `شهاداتي (${items.length})` : `My certificates (${items.length})`} />
                <Tab value="verify" icon={<VerifiedOutlinedIcon />} iconPosition="start" label={isAr ? 'التحقق من شهادة' : 'Verify certificate'} />
            </Tabs>

            {tab === 'verify' ? (
                <CertificateVerifyPanel embedded />
            ) : items.length === 0 ? (
                <DashboardPanelCard
                    title={isAr ? 'لا توجد شهادات بعد' : 'No certificates yet'}
                    subtitle={isAr ? 'تُصدر الشهادة تلقائياً بعد إكمال جميع دروس الدورة واجتياز كل اختباراتها بنجاح.' : 'Certificates are issued automatically after you complete all lessons and pass every exam in the course.'}
                    className="mx-0"
                >
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                        {isAr
                            ? 'أكمل جميع الدروس (الفيديو/الملفات) واجتز كل اختبارات الدورة بنجاح للحصول على الشهادة.'
                            : 'Complete all lessons (videos/files) and pass every course exam to receive your certificate.'}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                        <Button variant="contained" onClick={() => navigate('/my-courses')} sx={{ bgcolor: '#0D358C', '&:hover': { bgcolor: '#1d4ed8' } }}>
                            {isAr ? 'الانتقال إلى دوراتي' : 'Go to my courses'}
                        </Button>
                        <Button variant="outlined" onClick={() => setTab('verify')}>
                            {isAr ? 'التحقق من شهادة خارجية' : 'Verify an external certificate'}
                        </Button>
                    </Box>
                </DashboardPanelCard>
            ) : (
                <Box className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {items.map((cert, i) => {
                        const courseTitle = isAr
                            ? (cert.course_name_ar || cert.course_name || cert.course?.name_ar || 'شهادة')
                            : (cert.course_name_en || cert.course_name || cert.course?.name_en || 'Certificate');
                        const code = cert.verification_code;
                        const meta = [
                            formatDate(cert.issued_at, isAr),
                            cert.teacher_name ? `${isAr ? 'المدرس' : 'Instructor'}: ${cert.teacher_name}` : null,
                            code ? `${isAr ? 'رمز' : 'Code'}: ${code}` : null,
                        ].filter(Boolean).join(' · ');

                        return (
                            <ScrollReveal key={cert.id || i} delay={i * 60} direction="up" className="w-full">
                                <GridMediaCard
                                    title={courseTitle}
                                    description={meta}
                                    footer={
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start' }}>
                                            {cert.qr_image_url ? (
                                                <Box
                                                    component="img"
                                                    src={cert.qr_image_url}
                                                    alt={isAr ? 'رمز QR للتحقق' : 'Verification QR'}
                                                    sx={{ width: 88, height: 88, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                                                />
                                            ) : null}
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
                                                <Button size="small" variant="contained" onClick={() => download(cert.id)}>
                                                    {isAr ? 'تحميل PDF' : 'Download PDF'}
                                                </Button>
                                                <Button size="small" variant="outlined" onClick={() => openVerify(code)}>
                                                    {isAr ? 'التحقق من هذه الشهادة' : 'Verify this certificate'}
                                                </Button>
                                                {code ? (
                                                    <Button size="small" color="inherit" startIcon={<ContentCopyOutlinedIcon />} onClick={() => copyCode(code)}>
                                                        {isAr ? 'نسخ الرمز' : 'Copy code'}
                                                    </Button>
                                                ) : null}
                                            </Box>
                                        </Box>
                                    }
                                />
                            </ScrollReveal>
                        );
                    })}
                </Box>
            )}

            <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
        </Box>
    );
}

export default Certificates;
